import React, { useState, useEffect } from "react";
import LoadingSpinner from "../../components/Loading/LoadingSpinner";
import { AiOutlineEdit, AiOutlineDelete, AiOutlineUserAdd, AiOutlineSearch, AiOutlineEye } from 'react-icons/ai';
import { FaUsers } from "react-icons/fa6";
import { useAuth } from "../../context/Auth";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify"; 
import Swal from "sweetalert2"; 
import { api_url } from "../../data";
import userEvent from "@testing-library/user-event";
import RoleBasedComponent from "../../components/RoleBasedComponent";

const MemberManagement = () => {
    const navigate = useNavigate();
    const { requestWithAuth } = useAuth();
    const [registeredMembers, setRegisteredMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [deletingMemberId, setDeletingMemberId] = useState(null); 
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("")
    const [membersData, setMembersData] = useState(null);
    

    const [totalPages, setTotalPages] = useState(0);
    const [nextPage, setNextPage] = useState(null);
    const [prevPage, setPrevPage] = useState(null);

    const getRegisteredMembers = async (searchValue, pageSize, page) => {
        try {
            setLoadingMembers(true);
            const response = await requestWithAuth(`/members/?search=${searchValue}&page_size=${pageSize}&page=${page}`, 'GET');
            const response_data = response?.data;
            setRegisteredMembers(response_data.results?.members);
            setMembersData(response_data?.results)
            setNextPage(response_data?.next);
            setPrevPage(response_data?.prev);
            setTotalPages(Math.ceil(response_data?.count / pageSize));
        } catch (error) {
            toast.error("Failed to load members. Please try again.");
        } finally {
            setLoadingMembers(false);
        }
    };

    useEffect(() => {
        getRegisteredMembers(debouncedSearch, pageSize, currentPage);
    }, [debouncedSearch, pageSize, currentPage]);

    useEffect(()=>{
        const timer = setTimeout(()=>{
            setDebouncedSearch(search);
        }, 500);

        return () => clearTimeout(timer);
    }, [search])

    const initializeDeleteMember = (memberId) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                deleteMember(memberId);
            }
        });
    };

    const deleteMember = async (memberId) => {
        setDeletingMemberId(memberId); 
        try {
            await requestWithAuth(`/members/${memberId}/`, 'DELETE');
            toast.success("Member deleted successfully!");
          
            getRegisteredMembers(search, pageSize, currentPage);
        } catch (error) {
            console.error("Failed to delete member:", error);
            toast.error("Failed to delete member. Please try again.");
        } finally {
            setDeletingMemberId(null); 
        }
    };

    // Pagination Handlers
    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    
    const renderPageNumbers = () => {
        const pageNumbers = [];
        const totalNumbersToShow = 3; 

        let startPage = Math.max(1, currentPage - Math.floor(totalNumbersToShow / 2));
        let endPage = Math.min(totalPages, startPage + totalNumbersToShow - 1);

        
        if (endPage - startPage + 1 < totalNumbersToShow) {
            startPage = Math.max(1, endPage - totalNumbersToShow + 1);
        }

        if (startPage > 1) {
            pageNumbers.push(1);
            if (startPage > 2) {
                pageNumbers.push("...");
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pageNumbers.push("...");
            }
            pageNumbers.push(totalPages);
        }

        return pageNumbers.map((number, index) => (
            <button
                key={index}
                onClick={() => number !== "..." && handlePageClick(number)}
                disabled={number === "..."}
                className={`
                    min-w-[40px] h-10 flex items-center justify-center rounded-lg border text-sm font-medium transition-colors
                    ${number === currentPage
                        ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                        : number === "..."
                            ? "bg-white text-gray-400 border-gray-200 cursor-default"
                            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                    }
                `}
            >
                {number}
            </button>
        ));
    };

    // Helper function for role badge colors
    const getRoleBadgeColor = (role) => {
        switch (role?.toLowerCase()) {
            case 'elder':
                return 'bg-purple-100 text-purple-800 border-purple-200';
            case 'deacon':
                return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'member':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    // Helper function to get initials for avatar
    const getInitials = (fullName) => {
        const names = fullName.split(' ');
        return `${names[0]?.[0] || ''}${names[1]?.[0] || ''}`.toUpperCase();
    };

    const handleToggleStatus = async (user) => {
        if(user?.role === "Admin"){
            return toast.error("You cannot toggle the status of the admin user");
        }
        const newStatus = user?.status == "Active" ? "Inactive":"Active";
        try {
            
            const response = await requestWithAuth(
                `/members/${user.id}/`,
                "PUT",
                { membership_status: newStatus },
                { "Content-Type": "application/json" },
            );

            setRegisteredMembers(prevUsers =>
                prevUsers.map(u => (u.id === user.id ? { ...u, status: newStatus } : u))
            );
            toast.success(`Member ${user.full_name} has been set to ${newStatus}`);
        } catch (error) {
            
            toast.error("Failed to update user status");
        }
    };

    return (
        <div className="min-h-screen bg-gray-50/50 p-6">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Member Management</h1>
                    <p className="text-gray-600 mt-1">Manage church members and their information</p>
                </div>
                <RoleBasedComponent allowedRoles={['Admin']} fallback={null}>
                    <button
                        onClick={() => navigate('/members/new')}
                        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                        <AiOutlineUserAdd className="text-lg" />
                        Add New Member
                    </button>
                </RoleBasedComponent>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Total Members */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Members</p>
                            <p className="text-2xl font-bold text-gray-900">{membersData?.total_members}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <FaUsers className="text-blue-600 text-xl" />
                        </div>
                    </div>
                </div>

                {/* Active Members */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Active Members</p>
                            <p className="text-2xl font-bold text-green-600">
                                {membersData?.active_members}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                            <FaUsers className="text-green-600 text-xl" />
                        </div>
                    </div>
                </div>

                {/* Inactive Members */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Inactive Members</p>
                            <p className="text-2xl font-bold text-red-600">
                                {membersData?.inactive_members}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                            <FaUsers className="text-red-600 text-xl" />
                        </div>
                    </div>
                </div>

                {/* Church Leaders */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Church Leaders</p>
                            <p className="text-2xl font-bold text-purple-600">
                            {membersData?.church_leaders}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                            <FaUsers className="text-purple-600 text-xl" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Members Table Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Card Header with Search */}
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <h2 className="text-lg font-semibold text-gray-900">Member Accounts</h2>
                        <div className="relative">
                            <AiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search members..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all w-full sm:w-64"
                            />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    {loadingMembers ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="flex flex-col items-center gap-3">
                                <LoadingSpinner />
                                <p className="text-gray-600 text-sm">Loading members...</p>
                            </div>
                        </div>
                    ) : registeredMembers.length > 0 ? (
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">Member</th>
                                    <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">Position</th>
                                    <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">Status</th>
                                    <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm hidden md:table-cell">Contact</th>
                                    <RoleBasedComponent allowedRoles={['Admin']}>
                                        <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">Actions</th>
                                    </RoleBasedComponent>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {registeredMembers.map((member) => (
                                    <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="py-4 px-6">
                                            <div className="flex items-center gap-3">
                                                {
                                                    member.profile_picture ? (
                                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                                                            <img src={`${api_url}${member?.profile_picture}`} alt="" className="object-cover h-full w-full rounded-full" />
                                                        </div>
                                                    ):(
                                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                                                            {getInitials(member.full_name)}
                                                        </div>
                                                    )
                                                }
                                                
                                                <div className="min-w-0">
                                                    <p className="font-medium text-gray-900 truncate">
                                                        {member.full_name}
                                                    </p>
                                                    <p className="text-sm text-gray-500 truncate">{member.member_church_id}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-4 px-6">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(member.position)}`}>
                                                {member.position}
                                            </span>
                                        </td>
                                        <td className="py-4 px-6">
                                            <label htmlFor={`toggle-${member.id}`} className="flex items-center cursor-pointer">
                                                <div className="relative">
                                                    <RoleBasedComponent allowedRoles={['Admin']}>
                                                        <input
                                                            type="checkbox"
                                                            id={`toggle-${member.id}`}
                                                            className="sr-only"
                                                            checked={member.status === "Active"}
                                                            onChange={() => handleToggleStatus(member)}
                                                        />
                                                    </RoleBasedComponent>
                                                    <div className={`block w-10 h-6 rounded-full ${member?.status == "Active" ? 'bg-green-400' : 'bg-gray-300'}`}></div>
                                                    <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition transform ${member.status === "Active" ? 'translate-x-full' : ''}`}></div>
                                                </div>
                                                <div className={`ml-3 text-sm font-medium ${member.status == "Active" ? 'text-green-700' : 'text-red-700'}`}>
                                                    {member.status}
                                                </div>
                                            </label>
                                        </td>
                                        
                                        <td className="py-4 px-6 hidden md:table-cell">
                                            <div className="text-sm">
                                                <p className="text-gray-900 truncate">{member.email}</p>
                                                <p className="text-gray-500 truncate">{member.phone_number}</p>
                                            </div>
                                        </td>
                                        <RoleBasedComponent allowedRoles={['Admin']}>
                                            <td className="py-4 px-6">
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={() => navigate(`/members/${member.id}/profile`)}
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="View Profile"
                                                    >
                                                        <AiOutlineEye className="text-lg" />
                                                    </button>
                                                    
                                                    <button
                                                        onClick={() => initializeDeleteMember(member.id)}
                                                        disabled={deletingMemberId === member.id}
                                                        className={`p-2 rounded-lg transition-colors ${deletingMemberId === member.id ? 'text-gray-400 bg-gray-100 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'}`}
                                                        title="Delete Member"
                                                    >
                                                        {deletingMemberId === member.id ? (
                                                            <LoadingSpinner size="sm" /> 
                                                        ) : (
                                                            <AiOutlineDelete className="text-lg" />
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                        </RoleBasedComponent>
                                            
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                <FaUsers className="text-gray-400 w-12 h-12" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                {search ? 'No members found' : 'No members yet'}
                            </h3>
                            <p className="text-gray-500 text-center mb-6 max-w-md">
                                {search
                                    ? `No members match "${search}". Try adjusting your search.`
                                    : 'Get started by adding your first member to the system.'
                                }
                            </p>
                            {!search && (
                                <button
                                    onClick={() => navigate('/members/new')}
                                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                >
                                    <AiOutlineUserAdd className="text-lg" />
                                    Add First Member
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {registeredMembers.length > 0 && (
                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/30">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <select onChange={e=>setPageSize(e.target.value)} name="" id="" className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors">
                                    <option value="5">5</option>
                                    <option value="10" selected>10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                                <p className="text-sm text-gray-700">
                                    Showing page <span className="font-medium">{currentPage}</span> of{' '}
                                    <span className="font-medium">{totalPages}</span>
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handlePreviousPage}
                                    disabled={currentPage === 1}
                                    className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors
                                        ${currentPage === 1
                                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    Previous
                                </button>

                                <div className="flex items-center gap-1">
                                    {renderPageNumbers()}
                                </div>

                                <button
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages}
                                    className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors
                                        ${currentPage === totalPages
                                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MemberManagement;